/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { useTheme } from 'emotion-theming';
import { FC, useContext, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Page, Text } from '../..';
import { appStore, defaultState } from '../../../state';
import { matchedPlan, prepayPlan } from '../../../utils';
import {
  BillingStatusRes,
  Copy,
  Plan,
  ROUTES,
  ThemeProps,
} from '../../../types';
import { useGetBillingStatus, useGetPlans, useGetTvCode, useGetShowCancelButton, useGetShowRenewButton } from '../../../hooks';
import { PaymentMethodPayload } from 'braintree-web-drop-in';
import DashboardCancelSubscription from './DashboardCancelSubscription';

const isDateInFuture = (date: string) => {
  return new Date(date) > new Date();
}

const findCurrentPurchase = (billingStatus: BillingStatusRes) => {
  return billingStatus.purchases.find(purchase => purchase.product.id !== 0 && isDateInFuture(purchase.nextBillDate));
}

const subscriptionInfoData = ({
  billingStatus,
  fallbackCopy,
  localeCopy,
  payment,
  plans,
}: {
  billingStatus: BillingStatusRes;
  payment: PaymentMethodPayload | any;
  plans: Plan[];
} & Copy) => {
  const foundActivePremiumPurchases = billingStatus.purchases.find(
    /**
     * Premium plans have a product.id !== 0
     */
    (purchase: any) => purchase.cancelled === false && purchase.product.id !== 0
  );

  const pickedPlan = matchedPlan(
    foundActivePremiumPurchases?.product.id,
    plans
  );

  const userPlan = pickedPlan ? pickedPlan : (!pickedPlan && foundActivePremiumPurchases) ? prepayPlan(foundActivePremiumPurchases) : undefined;

  const billingStatusContainsPaymentInfo = (billingStatus: BillingStatusRes) => {
    return billingStatus?.paymentCardNumber && billingStatus?.paymentCardBrand;
  }

  const currentPurchase = findCurrentPurchase(billingStatus);

  const paymentDetailsData = (billingStatus: BillingStatusRes) => {
    if (billingStatusContainsPaymentInfo(billingStatus)) {
      return `${billingStatus.paymentCardBrand.toUpperCase()} Card **** **** **** ${billingStatus.paymentCardNumber}`;
    }
    const purchase = findCurrentPurchase(billingStatus);

    if (purchase) {
      if (purchase.paymentProvider === 'VODAFONEPAY') {
        return localeCopy?.subscriptionInfo?.vodafonePay || fallbackCopy.subscriptionInfo.vodafonePay;
      }
      if (purchase.paymentProvider === 'BRAINTREE') {
        return (localeCopy?.subscriptionInfo?.paypal || fallbackCopy.subscriptionInfo.paypal);
      }
    }
    return '';
  }

  if (userPlan) {
    if (currentPurchase?.paymentProvider === 'PRE_PAY_VOUCHER') {
      return [
        {
          title:
            localeCopy?.subscriptionInfo?.planType ||
            fallbackCopy.subscriptionInfo.planType,
          value:
            localeCopy?.plans['prepay']?.title ||
            fallbackCopy.plans['prepay'].title,
        }
        /* AZ-3692 - Hide subscription end date until proper solution implemented */
        // {
        //   title:
        //     localeCopy?.subscriptionInfo?.prePayPlanEnd ||
        //     fallbackCopy.subscriptionInfo.prePayPlanEnd,
        //   value:
        //     new Date(currentPurchase.nextBillDate).toLocaleDateString()
        //       .replace(/\//g, '-'),
        // }
      ];
    }

    return [
      {
        title:
          localeCopy?.subscriptionInfo?.planType ||
          fallbackCopy.subscriptionInfo.planType,
        value:
          localeCopy?.plans[userPlan.billingCycle.toLowerCase()]?.title ||
          fallbackCopy.plans[userPlan.billingCycle.toLowerCase()].title,
      },
      /* AZ-3692 - Hide subscription end date until proper solution implemented */
      // (!currentPurchase?.endBillDate)
      //   ? {
      //     title:
      //       localeCopy?.subscriptionInfo?.nextBillingDate ||
      //       fallbackCopy.subscriptionInfo.nextBillingDate,
      //     value: getNextBillingDate(billingStatus),
      //   }
      //   : null,
      (billingStatus.billingStatus === 'SUBSCRIBED' && !currentPurchase?.endBillDate)
        ? {
          title:
            localeCopy?.subscriptionInfo?.paymentType ||
            fallbackCopy.subscriptionInfo.paymentType,
          value: paymentDetailsData(billingStatus),
        }
        : null,
    ];
  } else {
    return [
      {
        title:
          localeCopy?.subscriptionInfo?.planType ||
          fallbackCopy.subscriptionInfo.planType,
        value:
          localeCopy?.subscriptionInfo?.noPremiumSubscription ||
          fallbackCopy.subscriptionInfo.noPremiumSubscription,
      },
    ];
  }
};

/* AZ-3692 - Hide subscription end date until proper solution implemented */
// const getNextBillingDate = (billingStatus: BillingStatusRes) => {
//   const date = billingStatus.purchases
//     .filter(purchase => purchase.product.id !== 0)
//     .filter(purchase => !purchase.cancelled)
//     .map(purchase => purchase.nextBillDate)[0]
//   if (date === null) return '';
//   return new Date(date).toLocaleDateString()
//     .replace(/\//g, '-');
// }

const isPrepayVoucherUser = (billingStatus: BillingStatusRes) => {
  return (findCurrentPurchase(billingStatus)?.paymentProvider === 'PRE_PAY_VOUCHER');
}

const renderSubscriptionInfo = ({
  billingStatus,
  fallbackCopy,
  localeCopy,
  payment,
  plans,
  theme,
}: {
  billingStatus: BillingStatusRes;
  payment: PaymentMethodPayload | any;
  plans: Plan[];
  theme: ThemeProps;
} & Copy) => (
  <div
    css={{
      aliignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginBottom: 40,
      width: '100%',
    }}>
    <div
      css={{
        borderBottom: `1.5px solid ${theme.colors.common.grey}`,
        marginBottom: 24,
        width: '100%',
      }}>
      <Text
        css={{
          fontSize: 14,
          textTransform: 'uppercase',
        }}
        color='secondary'>
        {localeCopy?.subscriptionInfo?.title ||
          fallbackCopy.subscriptionInfo.title}
      </Text>
    </div>
    {subscriptionInfoData({
      billingStatus,
      fallbackCopy,
      localeCopy,
      payment,
      plans,
    })
      .filter((data) => !!data)
      .map((data: any, index: number) => (
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            marginBottom: 16,
            width: '100%',
          }}
          key={index}>
          <Text>{data.title}</Text>
          <Text color='secondary'>{data.value}</Text>
        </div>
      ))}
    {!isPrepayVoucherUser(billingStatus) && <DashboardCancelSubscription
      fallbackCopy={fallbackCopy}
      localeCopy={localeCopy} />
    }
  </div>
);

interface DashboardProps extends Copy { }

const Dashboard: FC<DashboardProps> = ({ fallbackCopy, localeCopy }) => {
  const { dispatch, state } = useContext(appStore);
  const { billingStatus, plans, user, paymentButtons, config } = state;
  const { showConfirmButtons } = paymentButtons;
  const { blacklistedPlans } = config;
  const theme: ThemeProps = useTheme();
  const history = useHistory();

  const plansQuery = useGetPlans({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    plans,
    blacklistedPlans
  });

  const billingStatusQuery = useGetBillingStatus({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
  });

  const accountInfoData = [
    {
      title:
        localeCopy?.accountInfo?.emailAddress ||
        fallbackCopy.accountInfo.emailAddress,
      value: user.emailAddress,
    },
    {
      title:
        localeCopy?.accountInfo?.password || fallbackCopy.accountInfo.password,
      value: '•••••••',
    },
  ];

  const renderAccountInfo = () => (
    <div
      css={{
        aliignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        marginBottom: 40,
        width: '100%',
      }}>
      <div
        css={{
          borderBottom: `1.5px solid ${theme.colors.common.grey}`,
          marginBottom: 24,
          width: '100%',
        }}>
        <Text
          css={{
            fontSize: 14,
            textTransform: 'uppercase',
          }}
          color='secondary'>
          {localeCopy?.accountInfo?.title || fallbackCopy.accountInfo.title}
        </Text>
      </div>
      {accountInfoData.map((data: any, index: number) => (
        <div
          css={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            marginBottom: 16,
            width: '100%',
          }}
          key={index}>
          <Text>{data.title}</Text>
          <Text color='secondary'>{data.value}</Text>
        </div>
      ))}
      <Link
        css={{
          alignSelf: 'flex-end',
        }}
        to={ROUTES.USER_FORGOTTEN_PASSWORD}>
        <Text>
          {localeCopy?.forgottenPassword || fallbackCopy.forgottenPassword}
        </Text>
      </Link>
    </div>
  );

  const tvCodeQuery = useGetTvCode({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
  });

  const purchaseId = findCurrentPurchase(billingStatus)?.id || '';

  const showCancelButtonQuery = useGetShowCancelButton({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
    purchaseId,
    paymentButtons
  });

  const showRenewButtonQuery = useGetShowRenewButton({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    user,
    purchaseId,
    paymentButtons
  });

  // TODO: useQueries
  useEffect(() => {
    tvCodeQuery.refetch();
    plansQuery.refetch();
    billingStatusQuery.refetch()
      .then((res) => {
        if (res?.data) {
          showCancelButtonQuery.refetch();
          showRenewButtonQuery.refetch();
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    billingStatusQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmButtons])

  useEffect(() => {
    history.replace(ROUTES.DASHBOARD)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingStatus, paymentButtons, user])

  const handleClick = () => {
    /**
     * Clear state. The `ProtectedRoute` component that wraps this component
     * will take care of routing to login page
     */
    const { billingStatus, form, user, paymentButtons } = defaultState;
    dispatch({
      type: 'UPDATE_BILLING_STATUS',
      payload: {
        ...billingStatus,
      },
    });
    dispatch({
      type: 'UPDATE_USER',
      payload: {
        ...user,
      },
    });
    dispatch({
      type: 'UPDATE_FORM_DATA',
      payload: {
        ...form,
      },
    });
    dispatch({
      type: 'UPDATE_PAYMENT_BUTTONS',
      payload: {
        ...paymentButtons,
      },
    });
  };

  const handleClickRefreshTvCode = () => {
    tvCodeQuery.refetch();
  };

  return (
    <Page isReady={!billingStatusQuery.isLoading || !plansQuery.isLoading} title={localeCopy?.title || fallbackCopy.title}>
      {renderAccountInfo()}
      {!!billingStatus.purchases.length
        ? renderSubscriptionInfo({
          billingStatus,
          fallbackCopy,
          localeCopy,
          payment: user.payment,
          plans,
          theme,
        })
        : null}
      <div
        css={{
          borderBottom: `1.5px solid ${theme.colors.common.grey}`,
          marginBottom: 24,
          width: '100%',
        }}>
        <Text
          css={{
            fontSize: 14,
            textTransform: 'uppercase',
          }}
          color='secondary'>
          {localeCopy?.tvAccessCode?.title || fallbackCopy.tvAccessCode.title}
        </Text>
      </div>
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: 16,
          width: '100%',
        }}>
        <Text>
          {localeCopy?.tvAccessCode?.rowTitle ||
            fallbackCopy.tvAccessCode.rowTitle}
        </Text>
        {tvCodeQuery?.data?.tvToken ? (
          <Text weight='bold'>{`${tvCodeQuery?.data?.tvToken.substring(
            0,
            4
          )} - ${tvCodeQuery?.data?.tvToken.substring(4)}`}</Text>
        ) : null}
      </div>
      <div
        css={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          marginBottom: 40,
          width: '100%',
        }}>
        <Text
          css={{
            fontSize: 14,
            fontStyle: 'italic',
          }}
          color='secondary'>
          {localeCopy?.tvAccessCode?.twoMinutes ||
            fallbackCopy.tvAccessCode.twoMinutes}
        </Text>
        <Button
          css={{
            alignSelf: 'flex-end',
            color: 'black',
            fontSize: 14,
          }}
          onClick={handleClickRefreshTvCode}
          variant='link'>
          {localeCopy?.tvAccessCode?.getTvCode ||
            fallbackCopy.tvAccessCode.getTvCode}
        </Button>
      </div>

      <Button onClick={handleClick}
        onKeyUp={(e: any) => {
          if (e.key === 'Enter') {
            handleClick();
          }
        }}>
        {localeCopy?.logOutButton || fallbackCopy.logOutButton}
      </Button>
    </Page>
  );
};

export default Dashboard;